// The `evidence/source` READ transports — the five GETs that had no api module
// at all before this carve.
//
// WHY THIS FILE EXISTS RATHER THAN AN INLINE `queryFn` PER HOOK. Each of the
// hooks declared its own module-private `async function get…()` directly
// against the `@/utils/scribeHTTP` singleton, so the transport, the cache policy
// and the gate were one unit. Splitting them is what makes the S1 severance
// checkable: the route/verb/response assertions live in this module's own suite
// and the hooks' suites are then free to be about the gate, which is the half
// this carve actually changes. It also puts every `evidence/source` request in
// `data/api`, where the family's api dimension can see it.
//
// The routes deliberately share one module rather than getting one file each:
// they are reads with no request bodies and no shared state, and one file per
// GET would be five copies of the boilerplate below for ten lines of real code.
// `getBlockedDomains` joined them when the denylist read carved.
//
// `ScribeHttp` + the per-call `http()` are declared locally, matching every
// sibling api module in the repo (`data/api/sourceLists.ts` here,
// `agents/data/api/{inbox,harness,routines}.ts`, `commerce/data/api/trials.ts`).
// The per-call resolution is load-bearing — see `transcript-chips.ts`.
import { getScribeHttpClients } from '@heidi/api/client';

import type {
  BlockedDomainsResponse,
  ChatSourceItem,
  EvidenceSummarySuggestionResponse,
  PersonalizedEvidenceSuggestionsResponse,
  SourceRecommendationsResponse,
} from '../../contracts';

interface ScribeHttp {
  get<T>(url: string): Promise<{ data: T }>;
}

// `scribeHttpV2` base is /api/v2/ml-scribe/; scribe JWT injected by interceptor.
const http = (): ScribeHttp => getScribeHttpClients().scribeHttpV2;

/** Sources matched to the user's country and specialty. */
export async function getSourceRecommendations(): Promise<SourceRecommendationsResponse> {
  const { data } = await http().get<SourceRecommendationsResponse>(
    '/evidence/source-recommendations'
  );
  return data;
}

/** The Evidence banner's clinical-signal score for one session (GRO-2767). */
export async function getEvidenceSummarySuggestion(
  sessionId: string
): Promise<EvidenceSummarySuggestionResponse> {
  const { data } = await http().get<EvidenceSummarySuggestionResponse>(
    `/evidence/${sessionId}/summary-suggestion`
  );
  return data;
}

/**
 * Recent-work-informed evidence suggestion starters (GRO-2896).
 *
 * Experiment-gated on the BACKEND — control users get an empty suggestions list
 * rather than an error, which is why no flag gates this call client-side.
 */
export async function getPersonalizedEvidenceSuggestions(): Promise<PersonalizedEvidenceSuggestionsResponse> {
  const { data } = await http().get<PersonalizedEvidenceSuggestionsResponse>(
    '/chat/personalized-suggestions'
  );
  return data;
}

/**
 * The static source catalogue — every domain a source picker can offer, with
 * its regions, website types and authority score.
 *
 * Unauthenticated in the sense that matters here: the response is the same for
 * every user, which is why nothing gates the call and why the read that wraps
 * it holds the response for five minutes.
 */
export async function getSources(): Promise<ChatSourceItem[]> {
  const { data } = await http().get<ChatSourceItem[]>('/chat/sources');
  return data;
}

/**
 * The content-safety denylist every source picker rejects against inline.
 *
 * The `?? []` is load-bearing rather than defensive typing: the pickers build a
 * `Set` from this and call `.has()` on every keystroke, so a payload without
 * `domains` has to arrive here as an empty list and not as `undefined`. Moved
 * verbatim from the pre-move hook's `queryFn`, where it lived.
 */
export async function getBlockedDomains(): Promise<string[]> {
  const { data } = await http().get<BlockedDomainsResponse>('/chat/blocked-domains');
  return data.domains ?? [];
}
